import * as React from 'react';
import * as _ from 'underscore';
import {
  CheckBoxWithLabelWidget,
  TextFieldWidget
} from '@tableau/widgets';

import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { GrantLicenseModes } from '~src/ts/ServerApi/requests/SyncActiveDirectoryGroupRequest';

import { Messages } from '../../../codegen/Localize';
import { CheckboxState } from '../../../common/components/checkboxes/CheckboxState';
import { ReactActionDialogBodyProps } from '../../../commonInfra/dialogs/ReactActionDialog';
import { Filter } from '../../../ts/helpers/Filter';
import {
  GetUsersGroupMembershipRequest,
  IGetUsersGroupMembershipResult,
} from '../../../ts/ServerApi/requests/GetUsersGroupMembershipRequest';
import { GroupsGrid } from './GroupsGrid';
import { Groups } from '../Groups';
import { IGroupMember } from '../../../ts/ServerApi/types/IGroupMember';
import { IGroupInfo } from '../../../ts/models/IGroupInfo';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { ISiteUserInfo } from '../../../ts/models/ISiteUserInfo';
import { L10n } from '../../../ts/L10n/L10n';
import { Set } from '../../../ts/models/Set';
import {
  checkboxContainerStyle,
  dialogLabelStyles,
  textFieldStyles
} from './styles/EditGroupMembershipDialogStyles';
import { GroupsGridColumnKeys } from './GroupsGrid';
import WebHttpClient from '../../../httpClient/WebHttpClient';
import { ListResult } from '../../../ts/fetchers/ListResult';

export const GROUPS_SEARCH_BOX_TEST_ID = 'group-membership-groups-search-field';
export const ASSIGNED_GROUPS_CHECKBOX_TEST_ID = 'group-membership-assigned-groups';
export const DESCRIPTION_LABEL_TEST_ID = 'group-membership-description-label';
export const GET_GROUPS_PAGE_SIZE = 600;

export type EditGroupDetails = {
  groupName: string;
  groupCheckboxState: CheckboxState;
}

export type EditGroupMembershipDialogProps = {
  users: Set<ISiteUserInfo>;
};

export type EditGroupMembershipDialogState = {
  awaitServerResponse: boolean;
  clientGroupsState: _.Dictionary<string>;
  clientGroupsChanged: _.Dictionary<EditGroupDetails>;
  currentColumnSortOrder: IOrderClause;
  groupsRendered: IGroupInfo[];
  headerSelectionCheckboxState: CheckboxState;
  searchText: string;
  showOnlyAssignedGroupCheckboxChecked: boolean;
};

export type EditGroupMembershipActionDialogProps = ReactActionDialogBodyProps<EditGroupMembershipDialogProps, EditGroupMembershipDialogState>;

export class EditGroupMembershipDialog extends React.Component<EditGroupMembershipActionDialogProps, EditGroupMembershipDialogState> {
  public static readonly InputDebounceMilliseconds = 250;
  private groupsServerInfo: _.Dictionary<string>;
  private groupsClientChanges: _.Dictionary<EditGroupDetails>;
  private groupsClientCheckboxState: _.Dictionary<string>;

  constructor(props: EditGroupMembershipActionDialogProps){
    super(props);
    this.groupsServerInfo = {};
    this.groupsClientChanges = {};
    this.groupsClientCheckboxState = {};
    this.state = {
      awaitServerResponse: true,
      clientGroupsState: {},
      clientGroupsChanged: {},
      currentColumnSortOrder: {
        field: GroupsGridColumnKeys.GroupName,
        ascending: true
      },
      searchText: undefined,
      showOnlyAssignedGroupCheckboxChecked: false,
      groupsRendered: [],
      headerSelectionCheckboxState: CheckboxState.empty
    };
  }

  componentDidMount() {
    this.getInitialGroupsServerState();
  }

  private getInitialGroupsServerState() {
    const userIds = this.props.users.keys();
    if (userIds && userIds.length > 0) {
      // request group membership info from the server.
      this.getUsersGroupMembership()
        .then((result: IGetUsersGroupMembershipResult) => {
          // count how many selected users belong to each group.
          // e.g. {allUsers: 3, bigGroup: 2, ...}
          const groupMembershipCounts = _.countBy(result.members, (user: IGroupMember) => user.groupId);

          // for each group, compute the checkbox state.
          // e.g. {allUsers: 'checked', bigGroup: 'mixed', ...}
          const selectedUsersCount = this.props.users.size();
          _.each(groupMembershipCounts, (memberCount, groupId) => {
            this.groupsServerInfo[groupId] = this.getGroupCheckboxState(memberCount, selectedUsersCount);
          });

          this.groupsClientCheckboxState = _.clone(this.groupsServerInfo);
          this.setState({clientGroupsState: this.groupsClientCheckboxState});

          this.getGroupsFiltersSortOrder();
        });
    }
  }

  // Given the number of members in a group, and the number of selected users,
  // returns a string corresponding to the checkbox state.
  private getGroupCheckboxState(memberCount: number, selectedUsersCount: number): string {
    if (memberCount === selectedUsersCount) {
      return CheckboxState.checked;
    } else if (memberCount > 0) {
      return CheckboxState.mixed;
    } else {
      return CheckboxState.empty;
    }
  }

  //public for testing-purposes only
  public async getUsersGroupMembership(): Promise<IGetUsersGroupMembershipResult> {
    const usersGroupMembership = await WebHttpClient.sendRequest(new GetUsersGroupMembershipRequest({ userIds: this.props.users.keys() }));
    return usersGroupMembership;
  }

  //public for testing-purposes only
  public async getGroups(): Promise<IGroupInfo[]> {
    let allGroups: IGroupInfo[] = [];
    let moreItems: boolean = true;
    let startIndex: number = 0;

    while (moreItems) {
      const pagedGroups = await this.getGroupsAPICall(startIndex, GET_GROUPS_PAGE_SIZE);
      allGroups = allGroups.concat(pagedGroups.items);
      startIndex = startIndex + GET_GROUPS_PAGE_SIZE;
      moreItems = (startIndex < pagedGroups.total);
    }
    return allGroups;
  }

  //public for testing-purposes only
  public async getGroupsAPICall(startIndex: number, pageSize: number): Promise<ListResult<IGroupInfo>> {
    return await Groups.all().withOrder([this.state.currentColumnSortOrder])
    .addFilter(Filter.search(this.state.searchText))
    .getSlice(startIndex, pageSize);
  }

  private getGroupsFiltersSortOrder(): void {
    this.setState({ awaitServerResponse: true });
    this.getGroups()
      .then((result: IGroupInfo[]) => {
        let searchResultGroups: IGroupInfo[] = [];
        if (this.state.showOnlyAssignedGroupCheckboxChecked) {
          _.each(result, (group: IGroupInfo) => {
            if (this.groupsClientCheckboxState[group.id] && this.groupsClientCheckboxState[group.id] !== CheckboxState.empty) {
              searchResultGroups.push(group);
            }
          });
        } else {
          searchResultGroups = result;
        }
        this.setGroupsAndHeaderCheckboxStates(searchResultGroups);
      });
  }

  private setGroupsAndHeaderCheckboxStates(groups: IGroupInfo[]): void {
    this.setState({awaitServerResponse: false, groupsRendered: groups, headerSelectionCheckboxState: this.getHeaderCheckboxState(groups)});
  }

  private getHeaderCheckboxState(groups: IGroupInfo[]) {
    let emptyStateCheckboxes: number = 0;
    let checkedStateCheckboxes: number = 0;
    let allUsersGroupIncluded: boolean = false;

    if (groups.length === 0) {
      return CheckboxState.empty;
    }

    _.each(groups, (group: IGroupInfo) => {
      if (this.groupsClientCheckboxState[group.id] === undefined || this.groupsClientCheckboxState[group.id] === CheckboxState.empty) {
        emptyStateCheckboxes++;
      } else if (this.groupsClientCheckboxState[group.id] === CheckboxState.checked) {
        if (group.isAllUsersGroup) {
          allUsersGroupIncluded = true;
        } else {
          checkedStateCheckboxes++;
        }
      }
    });

    const groupsSize = allUsersGroupIncluded ? (groups.length - 1) : groups.length;
    if (checkedStateCheckboxes === groupsSize) {
      return CheckboxState.checked;
    } else if (emptyStateCheckboxes === groupsSize) {
      return CheckboxState.empty;
    } else {
      return CheckboxState.mixed;
    }
  }

  private renderLabelSection(): JSX.Element {
    const inputLabelProps: React.HTMLAttributes<HTMLDivElement> = {
      children: Messages.editGroupMembershipDescription({
        USER_NAME: L10n.elideIfLong(this.props.users.first().displayName),
        USER_COUNT: this.props.users.size(),
        USER_COUNT_LOC: L10n.localizedCount(this.props.users.size())
      }),
      style: dialogLabelStyles
    };

    return <div data-tb-test-id={DESCRIPTION_LABEL_TEST_ID} {...inputLabelProps} />;
  }

  private renderShowOnlyAssignedGroupsCheckBox(): JSX.Element {
    return <CheckBoxWithLabelWidget
      checked={this.state.showOnlyAssignedGroupCheckboxChecked}
      containerStyle={checkboxContainerStyle}
      disabled={false}
      handleChange={this.handleAssignedGroupsCheckboxChange}
      label={Messages.showOnlyAssignedGroups()}
      testId={ASSIGNED_GROUPS_CHECKBOX_TEST_ID}
    />;
  }

  private handleAssignedGroupsCheckboxChange = (checked: boolean) => {
    this.setState({showOnlyAssignedGroupCheckboxChecked: checked}, () => this.getGroupsFiltersSortOrder());
  }

  private renderGroupsSearchTextBox(): JSX.Element {
    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        this.handleCancelSearch();
      }
    };
    return <TextFieldWidget
      className={textFieldStyles}
      kind='search'
      label={undefined}
      onChange={this.handleSearchChange}
      onClear={this.handleCancelSearch}
      onKeyDown={onKeyDown}
      placeholder={Messages.searchForGroups()}
      style={{ width: '100%' }}
      testId={GROUPS_SEARCH_BOX_TEST_ID}
      value={this.state.searchText || ''}
    />;
  }

  private handleCancelSearch = () => {
    this.setState({ searchText: '' }, () => this.getGroupsFiltersSortOrder());
  }

  private handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchText: event.target.value });
    this.debounceUpdateSearchText();
  }

  private debounceUpdateSearchText = _.debounce(this.getGroupsFiltersSortOrder, EditGroupMembershipDialog.InputDebounceMilliseconds);

  private renderGroupsDataGrid(): JSX.Element {
    return <GroupsGrid
      awaitDisplayData={this.state.awaitServerResponse}
      currentColumnSortOrder={this.state.currentColumnSortOrder}
      displayData={this.state.groupsRendered}
      handleColumnCheckboxChange={this.onCheckboxChangedOnDataGridHeader}
      handleRowCheckboxChange={this.onCheckboxChangedOnDataGridRow}
      handleColumnSortOrderChange={this.changeColumnSort}
      headerCheckboxState={this.state.headerSelectionCheckboxState}
      rowCheckboxState={this.state.clientGroupsState}
      canUserManageGroup={this.canUserManageGroup}
    />;
  }

  private onCheckboxChangedOnDataGridRow = (rowGroup: IGroupInfo): void => {
    this.toggleRowCheckboxState(rowGroup);
    this.setState({clientGroupsState: this.groupsClientCheckboxState}, () => {
      this.setState({headerSelectionCheckboxState: this.getHeaderCheckboxState(this.state.groupsRendered)});
    });
  }

  private canUserManageGroup(rowGroup: IGroupInfo): boolean {
    if (rowGroup.grantLicenseMode === GrantLicenseModes.ON_LOGIN && !getSessionManagementServiceInstance().canSessionUserManageUsers()) {
      return false;
    }
    return true;
  }

  private toggleRowCheckboxState(rowGroup: IGroupInfo): void {
    if (rowGroup.isAllUsersGroup || !this.canUserManageGroup(rowGroup)) { return; }
    this.groupsClientCheckboxState[rowGroup.id] = CheckboxState.toggle(this.groupsClientCheckboxState[rowGroup.id]);

    // if the client & server state are equal, remove the group from client changes
    // to avoid sending no-op requests to the server.
    if (this.isClientAndServerStateEqualFor(rowGroup.id)) {
      delete this.groupsClientChanges[rowGroup.id];
    } else {
      this.groupsClientChanges[rowGroup.id] = {groupName: rowGroup.name, groupCheckboxState: this.groupsClientCheckboxState[rowGroup.id]};
    }
    this.props.updateDialogState({clientGroupsChanged: this.groupsClientChanges});
  }

  private onCheckboxChangedOnDataGridHeader = (): void => {
    const newHeaderCheckboxState = CheckboxState.toggle(this.state.headerSelectionCheckboxState.toString());
    this.toggleRowCheckboxOnHeaderCheckboxChange(newHeaderCheckboxState);
    this.setState({headerSelectionCheckboxState: newHeaderCheckboxState, clientGroupsState: this.groupsClientCheckboxState });
  }

  private toggleRowCheckboxOnHeaderCheckboxChange(headerCheckboxState: CheckboxState): void {
    _.each(this.state.groupsRendered, (group) => {
      if (headerCheckboxState === CheckboxState.checked) {
        if (this.groupsClientCheckboxState[group.id] !== CheckboxState.checked) {
          this.toggleRowCheckboxState(group);
        }
      } else {
        if (this.groupsClientCheckboxState[group.id] !== CheckboxState.empty) {
          this.toggleRowCheckboxState(group);
        }
      }
    });
  }

  // true if either client & server state are both checked, or client state is empty and server state is null.
  // note: server state doesn't use empty for empty groups, because of pagnination -- we don't know how many
  // groups there might be, so we only track the ones that have relevant membership, and treat the nulls as empty.
  private isClientAndServerStateEqualFor(groupId: string): boolean {
    return ((this.groupsClientCheckboxState[groupId] === this.groupsServerInfo[groupId])
      || (this.groupsClientCheckboxState[groupId] === CheckboxState.empty && !this.groupsServerInfo[groupId]));
  }

  private changeColumnSort = (field: string) => {
    const { currentColumnSortOrder } = this.state;
    const ascending = field === currentColumnSortOrder.field ? !currentColumnSortOrder.ascending : true;
    this.setState({currentColumnSortOrder: { field, ascending }}, () => this.getGroupsFiltersSortOrder());
  };

  public render(): JSX.Element {
    return <>
      {this.renderLabelSection()}
      {this.renderShowOnlyAssignedGroupsCheckBox()}
      {this.renderGroupsSearchTextBox()}
      {this.renderGroupsDataGrid()}
    </>;
  }
}
